import z from 'zod';

export type CvJsonComplianceIssue =
  | { kind: 'zod'; issues: z.core.$ZodIssue[] }
  | { kind: 'empty_required_string'; path: string; value: string }
  | { kind: 'internal'; message: string };

function unwrapModifiers(schema: z.ZodType): {
  optional: boolean;
  nullable: boolean;
  inner: z.ZodType;
} {
  let optional = false;
  let nullable = false;
  let s: z.ZodType = schema;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (s instanceof z.ZodOptional || s instanceof z.ZodExactOptional) {
      optional = true;
      s = s.unwrap() as z.ZodType;
      continue;
    }
    if (s instanceof z.ZodNullable) {
      nullable = true;
      s = s.unwrap() as z.ZodType;
      continue;
    }
    if (s instanceof z.ZodDefault || s instanceof z.ZodPrefault) {
      s = s.unwrap() as z.ZodType;
      continue;
    }
    break;
  }

  return { optional, nullable, inner: s };
}

function collectEmptyRequiredStrings(
  schema: z.ZodType,
  value: unknown,
  path: string[],
  out: { path: string; value: string }[],
): void {
  const { optional, nullable, inner } = unwrapModifiers(schema);

  if (optional) {
    if (value === undefined) {
      return;
    }
  }

  if (nullable && value === null) {
    return;
  }

  if (inner instanceof z.ZodObject) {
    const obj =
      value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
    const shape = inner.shape;
    for (const key of Object.keys(shape)) {
      const nextPath = [...path, key];
      try {
        collectEmptyRequiredStrings(shape[key] as unknown as z.ZodType, obj?.[key], nextPath, out);
      } catch {
        // Defensive: never break the caller on unexpected schema shapes
      }
    }
    return;
  }

  if (inner instanceof z.ZodArray) {
    const arr = Array.isArray(value) ? value : [];
    const el = inner.element;
    arr.forEach((item, i) => {
      try {
        collectEmptyRequiredStrings(el as unknown as z.ZodType, item, [...path, String(i)], out);
      } catch {
        // ignore
      }
    });
    return;
  }

  if (inner instanceof z.ZodString) {
    if (!optional && !nullable && typeof value === 'string') {
      if (value === '' || value === 'null') {
        out.push({ path: path.join('.') || '(root)', value });
      }
    }
  }
}

/**
 * Validates JSON returned by OpenAI (or similar) against a Zod object schema:
 * - Runs `safeParse` and collects Zod issues
 * - For required, non-nullable strings, flags `""` or the literal `"null"` (LLM artifact)
 *
 * Wrapped defensively so analysis never throws to the caller.
 */
export function analyseAiJsonCompliance(schema: z.ZodType, data: unknown): CvJsonComplianceIssue[] {
  const issues: CvJsonComplianceIssue[] = [];

  try {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      issues.push({ kind: 'zod', issues: parsed.error.issues });
    }

    try {
      const emptyStrings: { path: string; value: string }[] = [];
      collectEmptyRequiredStrings(schema, data, [], emptyStrings);
      for (const e of emptyStrings) {
        issues.push({
          kind: 'empty_required_string',
          path: e.path,
          value: e.value,
        });
      }
    } catch (e) {
      issues.push({
        kind: 'internal',
        message: `empty-string walk: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  } catch (e) {
    issues.push({
      kind: 'internal',
      message: `compliance: ${e instanceof Error ? e.message : String(e)}`,
    });
  }

  return issues;
}
